import { faTools } from "@fortawesome/pro-duotone-svg-icons"
import { faChevronLeft, faChevronRight, faTimes, faTree, faLeaf, faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Checkbox, Typography } from "antd"
import { useAccountSetup } from "./AccountSetupContext"
import { Division, INSTALLATION_DIVISIONS, MAINTENANCE_DIVISIONS, SNOW_DIVISIONS } from "./accountSetupReducer"
import styled from "styled-components"
import './AccountSetupModal.css'

const { Title, Text } = Typography

export type Props = {
  onBack: () => void
  onExit: () => void
}

const ServiceOption = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 2px solid ${props => props.$selected ? '#e85d04' : '#e8e8e8'};
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? 'rgba(232, 93, 4, 0.05)' : 'white'};

  &:hover {
    border-color: ${props => props.$selected ? '#e85d04' : '#d9d9d9'};
  }
`

const ServiceIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`

const ServiceContent = styled.div`
  flex: 1;
`

const ValidationMessage = styled.div`
  color: #e85d04;
  font-size: 13px;
  margin-top: 8px;
`

const AccountSetupServicesStep = ({ onBack, onExit }: Props) => {
  const { state, dispatch } = useAccountSetup()
  const { services } = state

  const hasAtLeastOneService = services.doInstallations || services.doMaintenance || services.doSnowServices

  const handleNext = () => {
    if (hasAtLeastOneService) {
      // Build divisions list based on selected services
      const newDivisions: Division[] = []

      if (services.doInstallations) {
        newDivisions.push(...INSTALLATION_DIVISIONS)
      }
      if (services.doMaintenance) {
        newDivisions.push(...MAINTENANCE_DIVISIONS)
      }
      if (services.doSnowServices) {
        newDivisions.push(...SNOW_DIVISIONS)
      }

      dispatch({ type: 'SET_DIVISIONS', payload: newDivisions })
      dispatch({ type: 'NEXT_STEP' })
    }
  }

  const toggleService = (field: 'doInstallations' | 'doMaintenance' | 'doSnowServices') => {
    dispatch({ type: 'UPDATE_SERVICES', field, value: !services[field] })
  }

  return (
    <div className="account-setup-modal-content">
      <div className="account-setup-progress-bar">
        <div className="account-setup-progress-fill" style={{ width: '50%' }} />
      </div>
      <button
        className="account-setup-exit-button"
        onClick={onExit}
        aria-label="Exit setup"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>

      <div className="account-setup-modal-left account-setup-form-container">
        <div className="account-setup-form-header">
          <div className="account-setup-modal-icon">
            <FontAwesomeIcon icon={faTools} size="2x" />
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
            What Kind of Services Do You Offer?
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Tell us about your services so we can set up your account with the right settings for your business.
          </Text>
        </div>

        <div className="account-setup-form" style={{ marginTop: 24 }}>
          <ServiceOption
            $selected={services.doInstallations}
            onClick={() => toggleService('doInstallations')}
          >
            <ServiceIcon $color="#52c41a">
              <FontAwesomeIcon icon={faTree} />
            </ServiceIcon>
            <ServiceContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Checkbox checked={services.doInstallations} />
                <Text strong>We do installations</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Landscape installations, hardscaping, lighting, water features, structures, and more.
              </Text>
            </ServiceContent>
          </ServiceOption>

          <ServiceOption
            $selected={services.doMaintenance}
            onClick={() => toggleService('doMaintenance')}
          >
            <ServiceIcon $color="#73d13d">
              <FontAwesomeIcon icon={faLeaf} />
            </ServiceIcon>
            <ServiceContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Checkbox checked={services.doMaintenance} />
                <Text strong>We do regular maintenance</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Lawn care, mowing, cleanups, irrigation servicing, and ongoing property maintenance.
              </Text>
            </ServiceContent>
          </ServiceOption>

          <ServiceOption
            $selected={services.doSnowServices}
            onClick={() => toggleService('doSnowServices')}
          >
            <ServiceIcon $color="#69b1ff">
              <FontAwesomeIcon icon={faSnowflake} />
            </ServiceIcon>
            <ServiceContent>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Checkbox checked={services.doSnowServices} />
                <Text strong>We handle snow services</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Snow removal, salting, de-icing, and winter property maintenance.
              </Text>
            </ServiceContent>
          </ServiceOption>

          {!hasAtLeastOneService && (
            <ValidationMessage>
              Please select at least one service type to continue.
            </ValidationMessage>
          )}

          <div className="account-setup-form-buttons" style={{ marginTop: 32 }}>
            <Button size="large" onClick={onBack}>
              <FontAwesomeIcon icon={faChevronLeft} /> Back
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              disabled={!hasAtLeastOneService}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </div>
      </div>

      <div className="account-setup-modal-right">
        <img
          src="/images/account-setup/step-4.png"
          alt="Services setup"
          className="account-setup-modal-image"
        />
        <div className="account-setup-modal-logo">
          <img src="/images/synkedup-dark.png" alt="SynkedUP" />
        </div>
        <div className="account-setup-modal-testimonial-wrapper">
          <div className="account-setup-modal-testimonial">
            <Text className="account-setup-testimonial-quote">
              "I am a crew leader and use SynkedUP every day; it's simple and easy. I love seeing the real-time progress report, photos, and material lists in the app."
            </Text>
          </div>
          <div className="account-setup-testimonial-author">
            <Text strong style={{ color: 'white' }}>Bryan Davidson</Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', display: 'block', fontSize: 14 }}>
              Greenview Planting Group
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountSetupServicesStep
